import React__default, { forwardRef, useState, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { CSSTransition } from 'react-transition-group';
import { u as useIsMobile } from '../../theme/MobileThemeProvider/useIsMobile-B-VP6bHU.js';
import { S as SecondaryView } from '../../SecondaryView/SecondaryView-BrYxk18J.js';
import { S as SummaryCardHeader } from '../SummaryCardHeader/SummaryCardHeader-rwexDDDQ.js';
import { E as EXPANDED_EXIT_TRANSITION_DURATION } from '../SummaryCardRow/utils-Bch8lY8w.js';
import { u as useIntl } from '../../LocalModules/storm-ui-intl/dist/esm/3rdPartyModules/react-intl/lib/src/components/useIntl-BLE3wELi-CV9fXVbx.js';
import { E as zoomOut } from '../../LocalModules/storm-ui-icons/dist/esm/localIcons-B_pWoZ1u-Bxh9jPb6.js';
import { T as TaktIdProvider } from '../../TaktIdContext/TaktIdContext-C6vdL-6Z.js';
import { c as createStormTaktId } from '../../TaktIdContext/TaktIdContext.utils-B0CzqlN8.js';

const ExpandedCardShroud = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-storm-ui-40021497__sc-mg048y-0"
})(["position:relative;z-index:2;box-sizing:border-box;display:flex;flex-grow:1;align-items:center;justify-content:center;background-color:", ";width:100%;min-height:100%;padding:", ";cursor:pointer;"], ({
  theme
}) => theme.SummaryCardExpanded.shroud.backgroundColor, ({
  theme
}) => theme.SummaryCardExpanded.margin);
const ExpandedCardWrapper = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-storm-ui-40021497__sc-mg048y-1"
})(["cursor:initial;box-sizing:border-box;display:flex;flex-direction:column;border:", ";border-radius:", ";padding:", ";background-color:", ";box-shadow:", ";max-height:calc(", "px - ", " * 2);opacity:0.2;transform:scale(0) translateY(-10%);@media (prefers-reduced-motion:no-preference){transition:all 0.25s ease-in-out;}", ""], ({
  theme
}) => theme.border.subtle.default, ({
  theme
}) => theme.border.radius.large, ({
  theme,
  $padding
}) => {
  var _theme$spacing;
  return (_theme$spacing = theme.spacing[$padding]) === null || _theme$spacing === void 0 ? void 0 : _theme$spacing.default;
}, ({
  theme,
  $backgroundColor
}) => theme.surface[$backgroundColor], ({
  theme
}) => theme.elevation.shadow.modal, ({
  $maxHeight
}) => $maxHeight !== null && $maxHeight !== void 0 ? $maxHeight : 0, ({
  theme
}) => theme.SummaryCardExpanded.margin, ({
  theme,
  $transitionStatus,
  $maxHeight
}) => {
  switch ($transitionStatus) {
    case 'entered':
      return css(["max-height:5000px;opacity:1;transform:scale(1);"]);
    case 'exiting':
      return css(["max-height:calc(", "px - ", " * 2);"], $maxHeight !== null && $maxHeight !== void 0 ? $maxHeight : 0, theme.SummaryCardExpanded.margin);
    default:
      return '';
  }
});
const ContentWrapper = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-storm-ui-40021497__sc-mg048y-2"
})(["width:100%;"]);
const ExpandedCard = /*#__PURE__*/forwardRef(({
  maxHeight,
  expandedCardProps,
  expandedState,
  setExpandedCard
}, ref) => {
  const {
    formatMessage
  } = useIntl();
  const isMobile = useIsMobile();
  // this is only here to make sure that we can animate the close after the expanded card has been exited
  const [isOpen, setIsOpen] = useState(false);
  const [internalExpandedCard, setInternalExpandedCard] = useState(undefined);
  useEffect(() => {
    switch (expandedState) {
      case 'collapsed':
        {
          setInternalExpandedCard(undefined);
          setIsOpen(false);
          break;
        }
      case 'transition':
        {
          setIsOpen(false);
          break;
        }
      case 'expanded':
        {
          setInternalExpandedCard(expandedCardProps);
          setIsOpen(true);
          break;
        }
    }
  }, [expandedCardProps, expandedState]);
  const closeExpandedCard = event => {
    setExpandedCard({
      cardProps: undefined,
      event
    });
  };
  const onKeyUp = event => {
    if (event.key === 'Escape') {
      closeExpandedCard(event);
    }
  };
  const onShroudClick = event => {
    // Only close if the click was directly on the shroud, not its children
    if (event.target === event.currentTarget) {
      closeExpandedCard(event);
    }
  };
  const {
    cardName,
    title,
    headerLevel,
    expandedContent,
    expandedHeaderContent,
    collapseButtonLabel,
    expandedPadding = 'base',
    backgroundColor = 'default',
    taktId,
    taktValue
  } = internalExpandedCard !== null && internalExpandedCard !== void 0 ? internalExpandedCard : {};
  if (isMobile) {
    return /*#__PURE__*/React__default.createElement(SecondaryView, {
      isOpen: isOpen,
      onClose: event => {
        if (event) {
          setExpandedCard({
            cardProps: undefined,
            event
          });
        }
        setIsOpen(false);
      },
      "aria-describedby": title,
      "aria-hidden": !isOpen
    }, /*#__PURE__*/React__default.createElement(SummaryCardHeader, {
      title: title !== null && title !== void 0 ? title : '',
      level: headerLevel,
      additionalContent: expandedHeaderContent,
      toggleButtonLabel: "",
      isExpandable: false,
      toggleIcon: zoomOut
    }), /*#__PURE__*/React__default.createElement(ContentWrapper, null, expandedContent));
  }
  return /*#__PURE__*/React__default.createElement(CSSTransition, {
    timeout: {
      enter: 0,
      // subtracting 10ms to reduce flicker on state updates
      exit: EXPANDED_EXIT_TRANSITION_DURATION - 10
    },
    appear: true,
    in: isOpen,
    nodeRef: ref
  }, status => {
    if (status === 'exited') return null;
    return /*#__PURE__*/React__default.createElement(TaktIdProvider, {
      taktId: taktId && `${taktId}-expanded`,
      taktValue: taktValue,
      fallbackId: createStormTaktId(`summary-card-${cardName}-expanded`)
    }, /*#__PURE__*/React__default.createElement(ExpandedCardShroud, {
      onKeyUp: onKeyUp,
      onClick: onShroudClick
    }, /*#__PURE__*/React__default.createElement(ExpandedCardWrapper, {
      ref: ref,
      $maxHeight: maxHeight,
      $transitionStatus: status,
      $padding: expandedPadding,
      $backgroundColor: backgroundColor,
      role: "dialog",
      "aria-describedby": title !== null && title !== void 0 ? title : cardName,
      "aria-hidden": !isOpen
    }, /*#__PURE__*/React__default.createElement(SummaryCardHeader, {
      title: title !== null && title !== void 0 ? title : '',
      level: headerLevel,
      additionalContent: expandedHeaderContent,
      isExpanded: true,
      isExpandable: true,
      toggleButtonLabel: collapseButtonLabel !== null && collapseButtonLabel !== void 0 ? collapseButtonLabel : formatMessage({
        id: 'collapse_button_label'
      }, {
        name: title
      }),
      toggleButtonTooltip: formatMessage({
        id: 'collapse_button_tooltip'
      }, {
        name: title
      }),
      toggleIcon: zoomOut,
      onToggleButtonClick: closeExpandedCard
    }), /*#__PURE__*/React__default.createElement(ContentWrapper, null, expandedContent))));
  });
});

export { ExpandedCard as E };
//# sourceMappingURL=ExpandedCard-DFCk1wqE.js.map
